import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, finalize } from 'rxjs';

import { AdminAuditService } from '../../core/services/admin-audit.service';
import { AdminAuditEntry } from '../../core/models/admin-audit.model';
import { AdminAuditDetailDialogComponent } from './admin-audit-detail-dialog.component';
import { CalendarDialogComponent } from '../../components/calendar-dialog.component';
import { RolePermissionService } from '../../core/services/role-permission.service'
import { TokenStorageService } from '../../core/services/token-storage.service';
import { SkeletonTableComponent } from '../../components/skeleton-table/skeleton-table.component';
import { ApplicationAdminsReportDialogComponent } from './application-admins-report-dialog.component';

@Component({
  selector: 'app-admin-audit',
  standalone: true,
  imports: [CommonModule, FormsModule, SkeletonTableComponent],
  templateUrl: './admin-audit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-audit.component.scss']
})
export class AdminAuditComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Source de vérité : tous les événements retournés par l'API */
  allEvents: AdminAuditEntry[] = [];

  /** Événements après application des filtres locaux */
  filteredEvents: AdminAuditEntry[] = [];

  /** Tranche affichée sur la page courante */
  paginatedEvents: AdminAuditEntry[] = [];

  /** Admins extraits des événements pour le filtre dropdown */
  adminsList: { id: string; displayName: string }[] = [];

  // ---- États UI ----
  isLoading = false;
  isAdmin = false;
  error: string | null = null;

  // ---- Pagination ----
  currentPage = 0;
  readonly PAGE_SIZE = 20;
  totalPages = 0;

  // ---- Filtres ----
  selectedOperationType = '';
  selectedAdminId = '';
  selectedPeriod = '';

  filterStartMs: number | null = null;
  filterEndMs: number | null = null;

  readonly operationTypes = [
    { value: 'AFFECTATION', label: 'Affectation rôle' },
    { value: 'RETRAIT', label: 'Retrait rôle' },
    { value: 'CREATE', label: 'Création' },
    { value: 'ACTIVATION', label: 'Activation compte' },
    { value: 'DESACTIVATION', label: 'Désactivation compte' },
    { value: 'UPDATE', label: 'Modification' },
    { value: 'SUPPRESSION', label: 'Suppression' },
    { value: 'ACTION_PASSWORD', label: 'Réinitialisation mot de passe' },
    { value: 'ACTION_EMAIL', label: 'Vérification / envoi email' },
    { value: 'ACTION_OTHER', label: 'Autre action admin' },
  ];

  constructor(
    private adminAuditService: AdminAuditService,
    private rolePermissionService: RolePermissionService,
    private tokenStorageService: TokenStorageService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.isAdmin = this.rolePermissionService.isSuperAdmin(this.tokenStorageService.getTokenFromStorage()?.toString());
    this.loadAudit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ══════════════════════════════════════════════════════════════════
  // ← AJOUT #2 : méthode d'ouverture du dialog de rapport
  // ══════════════════════════════════════════════════════════════════
  openDownloadReportDialog(): void {
    this.dialog.open(ApplicationAdminsReportDialogComponent, {
      width: '720px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      panelClass: 'applications-report-dialog-panel',
      autoFocus: false
    });
  }

  // ================================================================
  // CHARGEMENT
  // ================================================================

  loadAudit(): void {
    this.isLoading = true;
    this.error = null;

    this.adminAuditService.getAdminAudit()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false))
      )
      .subscribe({
        next: (events) => {
          this.allEvents = events;
          this.extractAdmins();
          this.currentPage = 0;
          this.applyFilters();
        },
        error: (err) => {
          console.error('Erreur chargement admin audit', err);
          this.error = 'Impossible de charger les événements. Veuillez réessayer.';
        }
      });
  }

  applyFilters(): void {
    this.filteredEvents = this.allEvents.filter(event => {
      const opMatch = !this.selectedOperationType
        || this.matchOperation(event, this.selectedOperationType);

      const adminMatch = !this.selectedAdminId || event.auteur === this.selectedAdminId;

      let dateMatch = true;
      if (this.filterStartMs !== null && this.filterEndMs !== null) {
        dateMatch = event.timestamp >= this.filterStartMs && event.timestamp <= this.filterEndMs;
      }

      return opMatch && adminMatch && dateMatch;
    });

    this.currentPage = 0;
    this.computePagination();
  }

  private matchOperation(event: AdminAuditEntry, filter: string): boolean {
    if (!filter) return true;

    if (event.operationType === filter) return true;
    if (event.eventCategory === filter) return true;

    if (!filter.startsWith('ACTION_')) return false;
    if (event.operationType !== 'ACTION') return false;

    const sub = event.actionSubType ?? '';
    if (!sub) return false;

    switch (filter) {
      case 'ACTION_PASSWORD':
        return ['UPDATE_PASSWORD', 'SEND_RESET_PASSWORD', 'UPDATE_CREDENTIALS',
                'FORGOT_PASSWORD', 'REGISTER_PASSWORD'].includes(sub);
      case 'ACTION_EMAIL':
        return ['VERIFY_EMAIL', 'SEND_VERIFY_EMAIL', 'UPDATE_EMAIL', 'VERIFY_PROFILE'].includes(sub);
      case 'ACTION_OTHER': {
        if (!sub) return true;
        return ![
          'UPDATE_PASSWORD', 'SEND_RESET_PASSWORD', 'UPDATE_CREDENTIALS',
          'FORGOT_PASSWORD', 'REGISTER_PASSWORD',
          'VERIFY_EMAIL', 'SEND_VERIFY_EMAIL', 'UPDATE_EMAIL', 'VERIFY_PROFILE',
        ].includes(sub);
      }
      default:
        return false;
    }
  }

  private computePagination(): void {
    this.totalPages = Math.ceil(this.filteredEvents.length / this.PAGE_SIZE) || 0;
    if (this.currentPage >= this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages - 1;
    }
    this.updatePage();
  }

  private updatePage(): void {
    const start = this.currentPage * this.PAGE_SIZE;
    this.paginatedEvents = this.filteredEvents.slice(start, start + this.PAGE_SIZE);
  }

  goToPage(page: number): void {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.updatePage();
  }

  goToPreviousPage(): void { this.goToPage(this.currentPage - 1); }
  goToNextPage(): void { this.goToPage(this.currentPage + 1); }

  getDisplayPages(): (number | string)[] {
    const total = this.totalPages;
    const cur = this.currentPage + 1;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const pages: (number | string)[] = [1];
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < total - 2) pages.push('...');
    pages.push(total);
    return pages;
  }

  private extractAdmins(): void {
    const map = new Map<string, string>();
    this.allEvents.forEach(e => {
      if (e.auteur && !map.has(e.auteur)) {
        map.set(e.auteur, e.auteur);
      }
    });
    this.adminsList = Array.from(map.entries())
      .map(([id, displayName]) => ({ id, displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }

  onOperationTypeChange(event: Event): void {
    this.selectedOperationType = (event.target as HTMLSelectElement).value;
    this.applyFilters();
  }

  onAdminChange(event: Event): void {
    this.selectedAdminId = (event.target as HTMLSelectElement).value;
    this.applyFilters();
  }

  onPeriodChange(): void {
    const now = new Date();
    this.filterStartMs = null;
    this.filterEndMs = null;

    switch (this.selectedPeriod) {
      case 'today': {
        const s = new Date(now); s.setHours(0, 0, 0, 0);
        const e = new Date(now); e.setHours(23, 59, 59, 999);
        this.filterStartMs = s.getTime();
        this.filterEndMs = e.getTime();
        break;
      }
      case 'week': {
        const s = new Date(now);
        const d = s.getDay();
        s.setDate(s.getDate() - (d === 0 ? 6 : d - 1));
        s.setHours(0, 0, 0, 0);
        const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23, 59, 59, 999);
        this.filterStartMs = s.getTime();
        this.filterEndMs = e.getTime();
        break;
      }
      case 'month': {
        const s = new Date(now.getFullYear(), now.getMonth(), 1); s.setHours(0, 0, 0, 0);
        const e = new Date(now.getFullYear(), now.getMonth() + 1, 0); e.setHours(23, 59, 59, 999);
        this.filterStartMs = s.getTime();
        this.filterEndMs = e.getTime();
        break;
      }
      case 'calendar':
        this.openCalendarDialog();
        return;
      default:
        break;
    }

    this.applyFilters();
  }

  openCalendarDialog(): void {
    const ref = this.dialog.open(CalendarDialogComponent, {
      width: '360px',
      panelClass: 'calendar-dialog-panel'
    });

    ref.afterClosed().subscribe((result: Date | null) => {
      if (result) {
        const day = new Date(result);
        const s = new Date(day); s.setHours(0, 0, 0, 0);
        const e = new Date(day); e.setHours(23, 59, 59, 999);
        this.filterStartMs = s.getTime();
        this.filterEndMs = e.getTime();
      } else {
        this.filterStartMs = null;
        this.filterEndMs = null;
        this.selectedPeriod = '';
      }
      this.applyFilters();
    });
  }

  resetFilters(): void {
    this.selectedOperationType = '';
    this.selectedAdminId = '';
    this.selectedPeriod = '';
    this.filterStartMs = null;
    this.filterEndMs = null;
    this.applyFilters();
  }

  formatDateTime(timestamp: number): string {
    if (!timestamp) return 'N/A';
    const d = new Date(timestamp);
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    const hh = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    const ss = d.getSeconds().toString().padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()} à ${hh}h${min}mn${ss}s`;
  }

  getOperationBadgeClass(operationType: string): string {
    const map: Record<string, string> = {
      AFFECTATION: 'badge success',
      RETRAIT: 'badge warning',
      CREATE: 'badge info',
      UPDATE: 'badge info',
      ACTION: 'badge secondary',
      SUPPRESSION: 'badge danger',
      DELETE: 'badge danger'
    };
    return map[operationType] ?? 'badge neutral';
  }

  getOperationLabel(operationType: string): string {
    const map: Record<string, string> = {
      AFFECTATION: 'Affectation rôle',
      RETRAIT: 'Retrait rôle',
      CREATE: 'Création',
      UPDATE: 'Modification',
      ACTION: 'Action admin',
      SUPPRESSION: 'Suppression',
      DELETE: 'Suppression'
    };
    return map[operationType] ?? operationType;
  }

  openDetailDialog(event: AdminAuditEntry): void {
    this.dialog.open(AdminAuditDetailDialogComponent, {
      width: '750px',
      panelClass: 'audit-detail-dialog-panel',
      data: event
    });
  }

  getEventLabel(event: AdminAuditEntry): string {
    if (event.eventCategory === 'ACTIVATION') return 'Activation';
    if (event.eventCategory === 'DESACTIVATION') return 'Désactivation';

    if (event.operationType === 'ACTION') {
      if (event.actionSubType && event.summary) return event.summary;
      return 'Action admin';
    }

    return this.getOperationLabel(event.operationType);
  }

  getEventBadgeClass(event: AdminAuditEntry): string {
    if (event.eventCategory === 'ACTIVATION') return 'badge success';
    if (event.eventCategory === 'DESACTIVATION') return 'badge danger';

    if (event.operationType === 'ACTION') {
      const sub = event.summary ?? '';
      if (sub.includes('Impersonation')) return 'badge danger';
      if (sub.includes('Suppression')) return 'badge danger';
      if (sub.includes('mot de passe') || sub.includes('Réinitialisation')) return 'badge warning';
      if (sub.includes('Activation') || sub.includes('Vérification')) return 'badge success';
      return 'badge secondary';
    }

    return this.getOperationBadgeClass(event.operationType);
  }
}